/**
 * useWebchatSession — localStorage-backed visitor session handle.
 *
 * Holds the { visitorId, token, expiresAt, conversationId } tuple across page
 * reloads. Expired tokens are discarded on mount; renewal is triggered either
 * when the hook consumer calls `renew` (resume flow) or when the user opens
 * the widget after expiration (new form submission).
 */

import { useCallback, useEffect, useState } from 'react';
import {
  webchatService,
  WebchatCreateSessionBody,
  WebchatSessionResponse,
} from '@/services/webchat.service';

const STORAGE_PREFIX = 'campanhaflow:webchat';
const RENEW_BUFFER_MS = 5 * 60 * 1000; // Renew if < 5 min left.

function storageKey(tenantSlug: string): string {
  return `${STORAGE_PREFIX}:${tenantSlug}`;
}

export interface WebchatSessionState {
  visitorId: string;
  conversationId: string;
  token: string;
  expiresAt: string;
}

export interface UseWebchatSessionApi {
  session: WebchatSessionState | null;
  start: (tenantSlug: string, body: WebchatCreateSessionBody) => Promise<WebchatSessionResponse>;
  renew: (tenantSlug: string) => Promise<WebchatSessionResponse | null>;
  /**
   * Ends the visitor session. Always clears the localStorage handle; when
   * `endOnServer` is true and we have a live token, also POSTs to
   * /public/webchat/session/close so the conversation is closed and any
   * running flow is cancelled. Defaults to true — only the passive
   * token-expiry cleanup and unit tests call it with false.
   */
  clear: (options?: { endOnServer?: boolean }) => Promise<void>;
}

export function useWebchatSession(tenantSlug: string | undefined): UseWebchatSessionApi {
  const [session, setSession] = useState<WebchatSessionState | null>(null);

  // Rehydrate on mount / tenant change. Expired tokens are treated as absent
  // but we keep the visitor_id around so resume can re-issue a token.
  useEffect(() => {
    if (!tenantSlug) return;
    const raw = safeReadLocalStorage(storageKey(tenantSlug));
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as WebchatSessionState;
      const expired = Date.parse(parsed.expiresAt) <= Date.now();
      if (expired) {
        // Still expose visitor_id via a partial session — callers can call
        // renew(). We keep token empty so consumers don't use it.
        setSession({ ...parsed, token: '' });
      } else {
        setSession(parsed);
      }
    } catch {
      safeRemoveLocalStorage(storageKey(tenantSlug));
    }
  }, [tenantSlug]);

  const persist = useCallback(
    (slug: string, next: WebchatSessionState) => {
      setSession(next);
      safeWriteLocalStorage(storageKey(slug), JSON.stringify(next));
    },
    []
  );

  const start = useCallback(
    async (slug: string, body: WebchatCreateSessionBody) => {
      // Include visitorId if we have one — the server treats it as a resume.
      const bodyWithResume = session?.visitorId
        ? { ...body, visitorId: session.visitorId }
        : body;
      const resp = await webchatService.createSession(slug, bodyWithResume);
      persist(slug, resp);
      return resp;
    },
    [session?.visitorId, persist]
  );

  const renew = useCallback(
    async (slug: string) => {
      if (!session?.visitorId) return null;
      // Resume path doesn't need consent/turnstile; server falls back to new
      // session if the visitor_id is unknown. We send placeholder name/phone
      // because the schema requires them — they are ignored on resume.
      try {
        const resp = await webchatService.createSession(slug, {
          name: 'visitor',
          phone: '0000000000',
          consent: true,
          visitorId: session.visitorId,
        });
        persist(slug, resp);
        return resp;
      } catch {
        return null;
      }
    },
    [session?.visitorId, persist]
  );

  const clear = useCallback(
    async (options?: { endOnServer?: boolean }) => {
      const endOnServer = options?.endOnServer ?? true;
      if (endOnServer && session?.token) {
        try {
          await webchatService.endSession(session.token);
        } catch {
          // Network/server failures shouldn't block the UX cleanup below —
          // the server TTL will clean up eventually.
        }
      }
      if (tenantSlug) safeRemoveLocalStorage(storageKey(tenantSlug));
      setSession(null);
    },
    [tenantSlug, session?.token]
  );

  // Passive renewal: if token will expire soon and we're online, grab a new one.
  useEffect(() => {
    if (!session?.token || !tenantSlug) return;
    const ttl = Date.parse(session.expiresAt) - Date.now();
    if (ttl <= RENEW_BUFFER_MS) {
      void renew(tenantSlug);
      return;
    }
    const handle = window.setTimeout(() => {
      void renew(tenantSlug);
    }, Math.max(ttl - RENEW_BUFFER_MS, 1000));
    return () => window.clearTimeout(handle);
  }, [session?.token, session?.expiresAt, tenantSlug, renew]);

  return { session, start, renew, clear };
}

function safeReadLocalStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWriteLocalStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Private mode / quota exceeded — widget still works for the session.
  }
}

function safeRemoveLocalStorage(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore.
  }
}
